import { Protocol as ControlProtocol } from '@studio/models/control';
import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Discovery } from './types.js';

/*
 * One Add Source row per physical camera. A camera that answers several
 * discovery protocols (Iris, NDI, fingerprinted RTSP, ONVIF) is listed once and
 * its transports become a choice on that row.
 *
 * Same camera = the same network host reported by the same bridge(s): an equal
 * normalized host AND an identical set of reporting bridges. Different bridges
 * never merge, so one private IP on two LANs stays two cameras. A discovery
 * with no host, no network transport or no reporting bridge (USB/PCIe capture,
 * an Iris mDNS row no bridge has seen) keeps its own row.
 *
 * Ambiguity guard: when one host on one bridge set advertises the same
 * transport twice (two NDI channels of an encoder, two Iris agents behind one
 * address), nothing at that host is merged, because the pairing is unknowable.
 * Each member keeps its own row, but the redundancy rules below still drop a
 * second RTSP-family route, unless RTSP or ONVIF is the repeated transport.
 *
 * Redundant ONVIF: a merged camera that has a fingerprint-matched RTSP stream
 * (an RTSP discovery carrying a registry modelId) drops its ONVIF member, so
 * the row offers one RTSP option, not "RTSP" plus "RTSP (ONVIF)". This mirrors
 * the encoder's ApplySameHostEnrichment ONVIF suppression (same_host_merge.go),
 * which only runs on the legacy discover path; the streaming discovery path
 * publishes the ONVIF row anyway. A brand-less ONVIF-only camera has no such
 * sibling and keeps its RTSP (ONVIF) row. A camera that also answers Iris keeps
 * its ONVIF member too: the HIL contract iris-enabled.spec.ts ("multi-add keeps
 * the ONVIF path for an Iris-capable camera") picks ONVIF on that row.
 *
 * Redundant RTSP: the converse for an RTSP stream the registry did NOT identify
 * (no modelId; unidentified or brand-only). Beside an ONVIF member it is the same
 * camera's second RTSP-family route, so the plain RTSP member is dropped and
 * RTSP (ONVIF) stays (Angekis Saber X showed both). Its brand identity moves to
 * the ONVIF member when ONVIF has none. Together the two rules leave each camera
 * one RTSP-family option, except the Iris and pinned exemptions above.
 *
 * Per rule: investigate-before-adding (ai.knowledge/global) — generalizes the
 * NDI/ONVIF same-host key of discovery-mismatch.ts and the Iris endpoint merge
 * in selector.tsx to every network transport; mismatch telemetry keeps its
 * narrower key so its event semantics do not change.
 */

/** Network transports a discovered camera can be added through. */
export type CameraTransport = 'iris' | 'ndi' | 'rtsp' | 'onvif';

/**
 * Default transport for a camera seen over several protocols; first wins:
 *   1. Iris: the camera's own Iris agent and our branded setup; its advertised
 *      model identity already takes precedence for dual-advertising cameras.
 *   2. RTSP: the fingerprint-matched registry stream (often needs credentials);
 *      the RTSP family is the most CPU-efficient path on the Bridge. An
 *      unidentified RTSP stream is not offered beside ONVIF:
 *      groupDiscoveriesByCamera drops it there in favour of RTSP (ONVIF).
 *   3. RTSP (ONVIF): the same stream, but needs an ONVIF probe for the stream
 *      URI. Not offered on a camera with a fingerprinted RTSP stream and no Iris
 *      agent: groupDiscoveriesByCamera drops it there (the streaming encoder
 *      path still publishes it).
 *   4. NDI: no stream credentials, but the Bridge transcodes it, which costs
 *      far more CPU than an RTSP-family stream.
 * A selected member, then the user's explicit choice, overrides the default.
 */
export const TRANSPORT_PRECEDENCE: readonly CameraTransport[] = ['iris', 'rtsp', 'onvif', 'ndi'];

/** Transport of a discovery, or undefined for local capture and unknown protocols. */
export const cameraTransport = (discovery: Discovery): CameraTransport | undefined => {
  if ('video' in discovery) {
    switch (discovery.video?.protocol) {
      case VideoProtocol.IRIS:
        return 'iris';
      case VideoProtocol.NDI:
        return 'ndi';
      case VideoProtocol.RTSP:
      case VideoProtocol.RTSPT:
      case VideoProtocol.RTSPLT:
        return 'rtsp';
      default:
        return undefined;
    }
  }

  return discovery.control?.protocol === ControlProtocol.ONVIF ? 'onvif' : undefined;
};

/** Lower-cased host without the DNS root dot; undefined when blank. */
const normalizeHost = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const host = value.trim().toLowerCase().replace(/\.$/, '');
  return host.length > 0 ? host : undefined;
};

/**
 * The fields `cameraHost` reads. Wider than this module's `Discovery` so the add
 * store's selection (the device-layer discovery union, audio included) fits too.
 */
type DiscoveryAddress = Readonly<{
  address?: string | undefined;
  video?: unknown;
  control?: unknown;
}>;

/**
 * Host of the camera behind a discovery: the resolved address, else the transport host.
 * Exported for the RTSP helper seed (`seedRtspHelperFromDiscovery`), which pre-fills
 * the IP field with the same host this grouping uses.
 */
export const cameraHost = (discovery: DiscoveryAddress): string | undefined => {
  const config = 'video' in discovery ? discovery.video : discovery.control;
  const host =
    typeof config === 'object' && config !== null && 'host' in config ? config.host : undefined;
  return normalizeHost(discovery.address) ?? normalizeHost(host);
};

/** Grouping key: host plus the exact bridge set; undefined keeps the discovery on its own row. */
const cameraKey = (discovery: Discovery): string | undefined => {
  const host = cameraHost(discovery);
  const bridges = [...new Set(discovery.agentIds ?? [])].sort();

  if (!cameraTransport(discovery) || !host || bridges.length === 0) {
    return undefined;
  }

  return `${host}|${bridges.join(',')}`;
};

const precedenceOf = (discovery: Discovery): number => {
  const transport = cameraTransport(discovery);
  return transport ? TRANSPORT_PRECEDENCE.indexOf(transport) : TRANSPORT_PRECEDENCE.length;
};

/** A fingerprint-matched RTSP stream: an RTSP discovery the registry identified (has a modelId). */
const isFingerprintedRtsp = (discovery: Discovery): boolean =>
  cameraTransport(discovery) === 'rtsp' && Boolean(discovery.modelId);

/**
 * Drops a camera's ONVIF member when the camera has a fingerprinted RTSP
 * stream: RTSP wins, as in the encoder's same_host_merge.go ONVIF suppression
 * (an RTSP sibling with a ModelId at the same host). Choosing RTSP (ONVIF) on
 * such a camera (BirdDog X1, PTZOptics Move SE) led into a broken add flow.
 * Only an RTSP member's modelId counts: the streaming path also stamps a
 * modelId on NDI and ONVIF rows, and those are not an RTSP alternative.
 *
 * Exemptions:
 *   - A camera with an Iris member keeps ONVIF. The HIL spec iris-enabled.spec.ts
 *     ("multi-add keeps the ONVIF path for an Iris-capable camera") requires the
 *     ordinary Bridge ONVIF add/auth path to stay selectable next to Iris
 *     firmware, and its lab camera (BirdDog X1 Ultra) is also fingerprinted.
 *     The encoder rule has no Iris case because Iris is not one of its sources.
 *   - A pinned (selected) ONVIF member stays: the selection commits it, so
 *     hiding its row would add a source the user can no longer see or untick.
 *     This keeps pickCameraTransport's "a selected member pins the row" contract.
 */
const dropRedundantOnvif = (
  group: readonly Discovery[],
  pinnedIds: ReadonlySet<Discovery['id']>
): Discovery[] => {
  const redundant =
    group.some(isFingerprintedRtsp) && !group.some((member) => cameraTransport(member) === 'iris');

  return redundant
    ? group.filter((member) => cameraTransport(member) !== 'onvif' || pinnedIds.has(member.id))
    : [...group];
};

/**
 * Copy of `onvif` carrying the brand identity of a dropped unidentified `rtsp`
 * sibling: brandId only when ONVIF has no brandId or modelId of its own, the raw
 * brand string only when ONVIF has none. No modelId: an unidentified stream has
 * none to carry.
 */
const withCarriedBrand = (onvif: Discovery, rtsp: Discovery): Discovery => {
  const brandId = !onvif.brandId && !onvif.modelId ? rtsp.brandId : undefined;
  const brand = !onvif.brand ? rtsp.brand : undefined;

  if (!brandId && !brand) {
    return onvif;
  }

  return { ...onvif, ...(brandId ? { brandId } : {}), ...(brand ? { brand } : {}) };
};

/**
 * Drops a camera's unidentified RTSP member (no modelId) when the camera also
 * answers ONVIF, so the row offers RTSP (ONVIF) instead of "RTSP" plus
 * "RTSP (ONVIF)". The fingerprinted case is dropRedundantOnvif's (ONVIF loses);
 * this only runs when that rule kept ONVIF and no RTSP member carries a modelId.
 *
 * A pinned (selected) RTSP member stays, for the same reason a pinned ONVIF
 * member does in dropRedundantOnvif. The ambiguity guard runs first, so a group
 * holds at most one RTSP member.
 */
const dropRedundantRtsp = (
  group: readonly Discovery[],
  pinnedIds: ReadonlySet<Discovery['id']>
): Discovery[] => {
  const onvif = group.find((member) => cameraTransport(member) === 'onvif');
  const rtsp = group.find((member) => cameraTransport(member) === 'rtsp');

  if (!onvif || !rtsp || rtsp.modelId || pinnedIds.has(rtsp.id)) {
    return [...group];
  }

  const enriched = withCarriedBrand(onvif, rtsp);
  return group
    .filter((member) => member !== rtsp)
    .map((member) => (member === onvif ? enriched : member));
};

/**
 * Groups discoveries by physical camera. Each group lists its members in
 * transport precedence order; groups keep first-seen order, so the result is
 * deterministic for a given input. `pinnedIds` (the selected discoveries) are
 * never dropped as a redundant ONVIF or RTSP transport.
 */
export const groupDiscoveriesByCamera = (
  discoveries: readonly Discovery[],
  pinnedIds: ReadonlySet<Discovery['id']> = new Set()
): Discovery[][] => {
  const groups: Discovery[][] = [];
  const byKey = new Map<string, Discovery[]>();

  for (const discovery of discoveries) {
    const key = cameraKey(discovery);
    const group = key === undefined ? undefined : byKey.get(key);

    if (group) {
      group.push(discovery);
      continue;
    }

    const created = [discovery];
    groups.push(created);
    if (key !== undefined) {
      byKey.set(key, created);
    }
  }

  return groups.flatMap((group) => {
    const transports = new Set(group.map(cameraTransport));

    // Ambiguity guard (see header): a repeated transport keeps every row separate.
    // The redundancy rules still apply unless the repeat is RTSP or ONVIF itself,
    // so a camera with two NDI channels (SimplTrack3) offers one RTSP-family row.
    if (transports.size !== group.length) {
      const rtspFamilyRepeated = (['rtsp', 'onvif'] as const).some(
        (transport) => group.filter((member) => cameraTransport(member) === transport).length > 1
      );
      const kept = rtspFamilyRepeated
        ? group
        : dropRedundantRtsp(dropRedundantOnvif(group, pinnedIds), pinnedIds);
      return kept.map((discovery) => [discovery]);
    }

    // Redundant ONVIF (see header): RTSP wins over ONVIF on a fingerprinted camera;
    // redundant RTSP: ONVIF wins over an unidentified RTSP stream.
    const kept = dropRedundantRtsp(dropRedundantOnvif(group, pinnedIds), pinnedIds);
    return [kept.sort((a, b) => precedenceOf(a) - precedenceOf(b))];
  });
};

/** The other transports of each discovery's camera; selection holds one member per camera. */
export const cameraSiblings = (
  groups: readonly (readonly Discovery[])[]
): Map<Discovery['id'], Discovery['id'][]> => {
  const siblings = new Map<Discovery['id'], Discovery['id'][]>();

  for (const group of groups) {
    for (const member of group) {
      siblings.set(
        member.id,
        group.filter((other) => other.id !== member.id).map((other) => other.id)
      );
    }
  }

  return siblings;
};

/** A camera row as rendered for the active protocol tab. */
export type CameraRow = Readonly<{
  /** Default-transport member; anchors the sort position so a transport switch never moves the row. */
  anchor: Discovery;
  /** Member the row shows, selects and commits. */
  active: Discovery;
  /** Transports offered on the row, in precedence order; one member means no choice. */
  members: readonly Discovery[];
}>;

/**
 * Member a camera row shows: a selected member first (a later, higher-ranked
 * advertisement must not re-key a selected row), then the user's explicit
 * transport choice, then the precedence default.
 */
export const pickCameraTransport = (
  members: readonly Discovery[],
  selectedIds: ReadonlySet<Discovery['id']>,
  chosenIds: ReadonlySet<Discovery['id']>
): Discovery | undefined =>
  members.find((member) => selectedIds.has(member.id)) ??
  members.find((member) => chosenIds.has(member.id)) ??
  members[0];

/**
 * Camera rows for a protocol tab: only members matching the tab are offered,
 * so the NDI tab lists each camera's NDI transport and "All" offers every one.
 */
export const toCameraRows = (
  groups: readonly (readonly Discovery[])[],
  matches: (discovery: Discovery) => boolean,
  selectedIds: ReadonlySet<Discovery['id']>,
  chosenIds: ReadonlySet<Discovery['id']>
): CameraRow[] =>
  groups.flatMap((group) => {
    const members = group.filter(matches);
    const [anchor] = members;

    if (!anchor) {
      return [];
    }

    return [
      { anchor, active: pickCameraTransport(members, selectedIds, chosenIds) ?? anchor, members }
    ];
  });
